import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import { Member, MemberLight, MemberPosition } from "../models/member/member";
import { MemberCreate } from "../models/member/member.create";
import { MemberUpdate } from "../models/member/member.update";
import { map, tap } from "rxjs/operators";
import { appState } from "src/app/core/state";
import { alphabetical } from "radash";
import { getCustomerNominative } from "src/app/support/customer";
import { omitUndefinedAndNullProperties } from "src/app/support/object";

@Injectable()
export class MemberBackend {
  private readonly http = inject(HttpClient);

  @UserNotify({
    successMessage: marker("Membro creato"),
    errorMessage: (errorResponse: HttpErrorResponse) => {
      const { statusCode } = errorResponse.error;

      if (statusCode === 409) {
        return marker("È stato raggiunto il massimo numero di membri");
      }

      return marker("Non è stato possibile creare il nuovo membro");
    }
  })
  create(request: MemberCreate): Observable<string> {
    return this.http.post<string>(
      `${environment.snoots.api.iam}/members`,
      request
    );
  }

  createOnboarding({
    account,
    tenant,
    member
  }: {
    account: string;
    tenant: string;
    member: Omit<Partial<Member>, "roles"> & { roles: string[] };
  }): Observable<Member> {
    return this.http.post<any>(
      `${environment.snoots.api.iam}/accounts/${account}/tenants/${tenant}/members`,
      member
    );
  }

  @UserNotify({
    successMessage: marker("Membro creato"),
    errorMessage: marker("Non è stato possibile creare il nuovo membro")
  })
  createApi(
    tenantId: string,
    request: { name: string; roles: string[] }
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.snoots.api.iam}/tenants/${tenantId}/api`,
      { ...request, type: "api" }
    );
  }

  @UserNotify({
    successMessage: marker("Membro creato"),
    errorMessage: marker("Non è stato possibile creare il nuovo membro")
  })
  createBox(
    tenantId: string,
    request: { name: string; roles: string[] }
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.snoots.api.iam}/tenants/${tenantId}/api`,
      { ...request, type: "box" }
    );
  }

  list(request: PageRequest): Observable<PaginatedResult<Member>> {
    let { page, pageSize, filterValues } = request;

    if (!filterValues?.orderBy) {
      filterValues = {
        ...filterValues,
        orderBy: "lastName",
        sort: "asc"
      };
    }

    return this.http.get<PaginatedResult<Member>>(
      `${environment.snoots.api.iam}/members`,
      {
        params: omitUndefinedAndNullProperties({
          p: page,
          s: pageSize,
          deleted: false,
          ...filterValues
        })
      }
    );
  }

  listActive(request: PageRequest): Observable<PaginatedResult<Member>> {
    return this.http
      .get<PaginatedResult<Member>>(`${environment.snoots.api.iam}/members`, {
        params: omitUndefinedAndNullProperties({
          p: request.page,
          s: request.pageSize,
          active: true,
          deleted: false,
          ...request.filterValues
        })
      })
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: alphabetical(response.docs, (member) =>
              getCustomerNominative(member)
            )
          };
        })
      );
  }

  numberOfActive(
    request: PageRequest
  ): Observable<PaginatedResult<{ subscribedMembers: number }>> {
    return this.http
      .get<{
        subscribedMembers: number;
      }>(`${environment.snoots.api.notification}/stats`)
      .pipe(
        map((result) => {
          return {
            docs: [{ subscribedMembers: result.subscribedMembers || 0 }],
            hasNextPage: false,
            hasPrevPage: false,
            limit: 0,
            nextPage: 1,
            page: 1,
            pagingCounter: 1,
            totalDocs: result.subscribedMembers,
            totalPages: result.subscribedMembers
          } as PaginatedResult<{ subscribedMembers: number }>;
        })
      );
  }

  listAllLight(): Observable<MemberLight[]> {
    return this.http
      .get<PaginatedResult<MemberLight>>(
        `${environment.snoots.api.iam}/members`,
        {
          params: {
            p: 1,
            s: 10000,
            deleted: false,
            light: true
          }
        }
      )
      .pipe(
        map((result) =>
          alphabetical(result.docs, (x) => getCustomerNominative(x))
        )
      );
  }

  listActiveLight(
    request: PageRequest,
    tenant?: string
  ): Observable<PaginatedResult<MemberLight>> {
    return this.http
      .get<PaginatedResult<MemberLight>>(
        `${environment.snoots.api.iam}/members`,
        {
          params: omitUndefinedAndNullProperties({
            p: request.page,
            s: request.pageSize,
            active: true,
            deleted: false,
            light: true,
            tenant,
            ...request.filterValues
          })
        }
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: alphabetical(response.docs, (member) =>
              getCustomerNominative(member)
            )
          };
        })
      );
  }

  listActiveAll(): Observable<Member[]> {
    return this.listActive({
      page: 1,
      pageSize: 100000,
      filterValues: { type: "human", system: "false", disabled: "false" }
    }).pipe(map((result) => result.docs));
  }

  listActiveAllLight(
    orderInverted: boolean = false,
    tenant?: string
  ): Observable<MemberLight[]> {
    return this.listActiveLight(
      {
        page: 1,
        pageSize: 100000,
        filterValues: { type: "human", system: "false", disabled: "false" }
      },
      tenant
    ).pipe(
      map((result) =>
        alphabetical(result.docs, (member) =>
          getCustomerNominative(member, orderInverted)
        )
      ),
      tap((members: MemberLight[]) => {
        appState.members = members;
      })
    );
  }

  listActiveAllByPositionLight(
    position?: MemberPosition
  ): Observable<MemberLight[]> {
    return this.listActiveLight({
      page: 1,
      pageSize: 100000,
      filterValues: {
        type: "human",
        system: "false",
        disabled: "false",
        position
      }
    }).pipe(
      map((result) =>
        alphabetical(result.docs, (member) =>
          getCustomerNominative(member, true)
        )
      ),
      tap((members: MemberLight[]) => {
        appState.members = members;
      })
    );
  }

  loadByEmail(
    email: string,
    accountId: string
  ): Observable<PaginatedResult<Member>> {
    return this.http.get<PaginatedResult<Member>>(
      `${environment.snoots.api.iam}/accounts/${accountId}/members`,
      {
        params: {
          email
        }
      }
    );
  }

  load(id: string): Observable<Member> {
    return this.http.get<Member>(`${environment.snoots.api.iam}/members/${id}`);
  }

  loadLight(id: string): Observable<MemberLight> {
    return this.http.get<MemberLight>(
      `${environment.snoots.api.iam}/members/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  loadMe(): Observable<Member> {
    return this.http.get<Member>(`${environment.snoots.api.iam}/members/me`);
  }

  loadMeWithToken({
    memberToken
  }: {
    memberToken: string;
  }): Observable<Member> {
    return this.http.get<Member>(`${environment.snoots.api.iam}/members/me`, {
      headers: {
        Member: memberToken
      }
    });
  }

  loadForAnalytics(id: string): Observable<{ id: string; name: string }> {
    return this.http
      .get<Member>(`${environment.snoots.api.iam}/members/${id}`)
      .pipe(
        map((member) => {
          return {
            ...member,
            name: `${member.firstName} ${member.lastName}`
          };
        })
      );
  }

  @UserNotify({
    successMessage: marker("Membro cancellato"),
    errorMessage: marker(
      "Non è stato possibile cancellare il membro selezionato"
    )
  })
  delete(id: string): Observable<string> {
    return this.http.delete<string>(
      `${environment.snoots.api.iam}/members/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Membro aggiornato"),
    errorMessage: marker(
      "Non è stato possibile aggiornare il membro selezionato"
    )
  })
  update(id: string, request: Partial<MemberUpdate>): Observable<string> {
    return this.http.patch<string>(
      `${environment.snoots.api.iam}/members/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Invito rispedito"),
    errorMessage: marker("Non è stato possibile rispedire l'invito")
  })
  sendInvite(id: string): Observable<string> {
    return this.http.put<string>(
      `${environment.snoots.api.iam}/members/${id}/invite`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Utente disabilitato"),
    errorMessage: marker("Non è stato possibile disabilitare l'utente")
  })
  disable(id: string): Observable<string> {
    return this.http.put<string>(
      `${environment.snoots.api.iam}/members/${id}/disable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Utente abilitato"),
    errorMessage: marker("Non è stato possibile abilitare l'utente")
  })
  enable(id: string): Observable<string> {
    return this.http.put<string>(
      `${environment.snoots.api.iam}/members/${id}/enable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Password cambiata"),
    errorMessage: marker("Non è stato possibile cambiarae la password")
  })
  resetPassword(id: string): Observable<{ password: string }> {
    return this.http.put<{ password: string }>(
      `${environment.snoots.api.iam}/apis/${id}/reset-password`,
      {}
    );
  }
}
